/* ------------------------ Functional imports start ------------------------ */
import { createContext, type ReactElement, useEffect, useState } from "react";
import LogTool from "../../logger/logTools"
import { useUserContext } from "../../utils/context"
import { useUnreadMessagesCount } from "./hooks/useChatQueries"
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ChatSystemGlobalStateProvider(props: {children: React.ReactNode}): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({context: 'ChatSystemGlobalStateProvider', enable: true, logLevel: 'warn'})
  const { user } = useUserContext()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- React Query Hooks --------------------------- */
  // Disable unread messages count for guest users - they don't have access to chat
  const isGuestUser = user?.isGuestUser ?? false
  const { data: fetchedUnreadCount } = useUnreadMessagesCount(Boolean(user) && !isGuestUser)
  /* ---------------------------- React Query Hooks End ----------------------- */

  /* ---------------------------- Data states start --------------------------- */
  // note: a value of -1 indicates that the unreadMessagesTotal was not fetched yet
  const [unreadMessagesTotal, setUnreadMessagesTotal] = useState<number>(-1)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    // Update local state when data is fetched
    if (fetchedUnreadCount !== undefined) {
      log.info('Success fetching total unread messages count', fetchedUnreadCount)
      setUnreadMessagesTotal(fetchedUnreadCount)
    }
  }, [fetchedUnreadCount])
  /* ------------------------------- Effects end ------------------------------ */

  log.debug('UnreadMessagesTotal ->', unreadMessagesTotal)


  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <ChatSystemGlobalStates.Provider
      value={{
        unreadMessagesTotal: unreadMessagesTotal > 0 ? unreadMessagesTotal : 0,
        setUnreadMessagesTotal: setUnreadMessagesTotal,
      }}
    >
      {props.children}
    </ChatSystemGlobalStates.Provider>
  )
}

interface ISharepointGlobalStates {
  unreadMessagesTotal: number,
  setUnreadMessagesTotal: React.Dispatch<React.SetStateAction<number>>
}

export const ChatSystemGlobalStates = createContext<ISharepointGlobalStates>({
  unreadMessagesTotal: 0,
  setUnreadMessagesTotal: () => null
})